import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isAfter,
  isBefore,
  isFuture,
  isPast,
  parseISO,
} from 'date-fns';
import {
  APPRENTICESHIP_PRODUCT_CODES,
  CreditRedemptionDto,
  CreditRedemptionInformation,
  CreditRedemptionState,
  CreditRedemptionTimeLeft,
  CreditRedemptionTimeUnit,
  CreditRedemptionViewModel,
  ProductCode,
} from '@mkp/shared/models';
import { isSlotType } from './credit.util';
import { isFreeProductCode } from './product.util';

export const isActiveSlotRedemption = (creditRedemption: CreditRedemptionDto): boolean => {
  const productType = creditRedemption._embedded.credit?.product?.type;
  return (
    !!productType &&
    isSlotType(productType) &&
    creditRedemption._state === CreditRedemptionState.Active
  );
};

export const isActiveCreditRedeem = (
  creditRedeem: CreditRedemptionViewModel | undefined
): boolean => creditRedeem?.information.status === CreditRedemptionState.Active;

export const isFreeProduct = (creditRedeem: CreditRedemptionViewModel | undefined): boolean => {
  const productCode = creditRedeem?.information?.productCode as ProductCode;
  return isFreeProductCode(productCode);
};

export const isActiveFreeProduct = (creditRedeem: CreditRedemptionViewModel | undefined): boolean =>
  isActiveCreditRedeem(creditRedeem) && isFreeProduct(creditRedeem);

export const isApprenticeshipProduct = (
  creditRedeem: CreditRedemptionDto | CreditRedemptionViewModel | undefined
): boolean => {
  const productCode =
    (creditRedeem as CreditRedemptionViewModel)?.information?.productCode ??
    (creditRedeem as CreditRedemptionDto)?._embedded?.credit?.product?.type;
  return APPRENTICESHIP_PRODUCT_CODES.includes(productCode as ProductCode);
};

export const hasActiveApprenticeshipProduct = (
  creditRedemptions: (CreditRedemptionDto | CreditRedemptionViewModel)[] | undefined
): boolean =>
  !!creditRedemptions?.some(
    (redemption) => isActive(redemption) && isApprenticeshipProduct(redemption)
  );

// TODO: is "draft" a possibility ? Otherwise replace with !isActive()
export const isCreditInactive = (creditRedeem: CreditRedemptionViewModel | undefined): boolean =>
  !!creditRedeem?.information?.status &&
  [CreditRedemptionState.Inactive, CreditRedemptionState.Draft].includes(
    creditRedeem.information.status
  );

export const isStoppedOrExpired = (creditRedeem: CreditRedemptionViewModel | undefined): boolean =>
  !creditRedeem ||
  [CreditRedemptionState.Stopped, CreditRedemptionState.Expired].includes(
    creditRedeem.information.status
  );

export const isCreditUndefinedOrStoppedOrExpiredOrInactive = (
  creditRedeem: CreditRedemptionViewModel | undefined
): boolean =>
  !creditRedeem ||
  [
    CreditRedemptionState.Stopped,
    CreditRedemptionState.Expired,
    CreditRedemptionState.Inactive,
  ].includes(creditRedeem.information.status);

// TODO: remove when we no longer have imported job ads - CDP-1098
// Structural rather than Vacancy: naming @mkp/vacancy/models from a scope:shared util puts this
// library in a cycle, and a cycle suppresses the boundary warning on the same import line.
export const isImported = (
  vacancy: { createdBy: string; _embedded: { creditRedemptions: readonly unknown[] } },
  createdByIdsForImportedJobAds: string[]
): boolean =>
  createdByIdsForImportedJobAds.includes(vacancy.createdBy) &&
  vacancy._embedded.creditRedemptions.length === 0;

// These predicates accept both the DTO (wire-format string dates) and the view model (parsed
// Dates), so normalize before comparing.
// Only stoppedAt arrives as '' from the API; start and end are always populated. A !! guard on
// them would be inert anyway — isFuture/isBefore both return false for the Invalid Date that
// parseISO('') yields.
const toDate = (value: string | Date): Date => (value instanceof Date ? value : parseISO(value));

// "scheduled" = pending, not canceled, and still set to go live in the future. A bare _state check
// lets an overdue or canceled pending masquerade as scheduled, which 404s the boost route
// (vacancy-without-paid-product.guard.ts) and hides the publish button (publish-button.service.ts)
export const isScheduled = (
  credit: CreditRedemptionDto | CreditRedemptionViewModel | undefined
): boolean =>
  credit?._state === CreditRedemptionState.Pending &&
  !credit.stoppedAt &&
  isFuture(toDate(credit.start));

// active wins; else a scheduled (future, not-canceled pending) one; else the latest by start.
// filter() hands sort() a fresh array, so the caller's array is never reordered
export const getRelevantRedemption = (
  redemptions: (CreditRedemptionViewModel | undefined)[]
): CreditRedemptionViewModel | undefined => {
  const sorted = redemptions
    .filter((redemption): redemption is CreditRedemptionViewModel => !!redemption)
    .sort((redemption1, redemption2) => redemption2.start.getTime() - redemption1.start.getTime());

  return sorted.find(isActiveCreditRedeem) ?? sorted.find(isScheduled) ?? sorted[0];
};

// the ad has gone online at least once when it has a redemption that got past draft and pending.
// A redemption canceled before its start never went online, yet the API reports it as Stopped, so
// the stoppedAt/start comparison is what separates "stopped after running" from "canceled early"
export const hasBeenPublished = (
  creditRedemptions: (CreditRedemptionDto | CreditRedemptionViewModel)[] | undefined
): boolean =>
  !!creditRedemptions?.some(
    (redemption) =>
      redemption._state !== CreditRedemptionState.Pending &&
      redemption._state !== CreditRedemptionState.Draft &&
      (!redemption.stoppedAt || isAfter(toDate(redemption.stoppedAt), toDate(redemption.start)))
  );

export const isActive = (
  credit: CreditRedemptionDto | CreditRedemptionViewModel | undefined
): boolean =>
  !!credit &&
  isFuture(toDate(credit.end)) &&
  !credit.stoppedAt &&
  credit._state !== CreditRedemptionState.Pending;
// an expired credit redeem has an expired end date but no stoppedAt date
export const isExpired = (credit: CreditRedemptionDto | CreditRedemptionViewModel): boolean =>
  isBefore(toDate(credit.end), new Date()) && !credit.stoppedAt;
// a stopped credit redeem has an expired end date AND a stoppedAt date
export const isStopped = (credit: CreditRedemptionDto | CreditRedemptionViewModel): boolean =>
  !!credit?.stoppedAt;

// the API keeps an online redemption active past its slot credit's validTo, so _state cannot tell
export const hasExpiredSlotContract = (
  creditRedeem: CreditRedemptionViewModel | null | undefined
): boolean => {
  const credit = creditRedeem?._embedded.credit;
  const productType = credit?.product?.type;
  return (
    !!creditRedeem &&
    isActiveCreditRedeem(creditRedeem) &&
    !!productType &&
    isSlotType(productType) &&
    !!credit?.validTo &&
    isPast(toDate(credit.validTo))
  );
};

export const getInformation = (
  creditRedeem: CreditRedemptionDto
): Pick<CreditRedemptionInformation, 'status' | 'relevantDate' | 'timeLeft'> => {
  if (isScheduled(creditRedeem)) {
    return {
      status: CreditRedemptionState.Pending,
      relevantDate: format(parseISO(creditRedeem.start), 'dd.MM.yyyy'),
      timeLeft: null,
    };
  } else if (isActive(creditRedeem)) {
    return {
      status: CreditRedemptionState.Active,
      relevantDate: format(parseISO(creditRedeem.start), 'dd.MM.yyyy'),
      timeLeft: timeLeft(creditRedeem),
    };
  } else if (isStopped(creditRedeem)) {
    return {
      status: CreditRedemptionState.Stopped,
      relevantDate: format(parseISO(creditRedeem.stoppedAt ?? ''), 'dd.MM.yyyy'),
      timeLeft: null,
    };
  } else if (isExpired(creditRedeem)) {
    return {
      status: CreditRedemptionState.Expired,
      relevantDate: format(parseISO(creditRedeem.end), 'dd.MM.yyyy'),
      timeLeft: null,
    };
  }

  // should be unreachable
  return {
    status: CreditRedemptionState.Inactive,
    relevantDate: null,
    timeLeft: null,
  };
};

export const timeLeft = (credit: CreditRedemptionDto): CreditRedemptionTimeLeft | null => {
  if (!credit) {
    return null;
  }

  const date = new Date();
  const matrix: { [key in string]: number } = {
    DAY: differenceInDays(parseISO(credit.end), date),
    HOUR: differenceInHours(parseISO(credit.end), date),
    MINUTE: differenceInMinutes(parseISO(credit.end), date),
    SECOND: differenceInSeconds(parseISO(credit.end), date),
  };

  const unit = Object.keys(matrix).find((unit) => matrix[unit] > 0) as CreditRedemptionTimeUnit;

  return { unit, value: matrix[unit] };
};
